import React, { useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import TextareaAutosize from 'react-autosize-textarea';
import drawUtils from '../pages/LessonPage/drawUtils';
import { DeleteForever, MoreVert } from '@material-ui/icons';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';
import TextFormatIcon from '@material-ui/icons/TextFormat';
import { makeStyles } from '@material-ui/core/styles';
import StyledMenu from '../MyComponents/Drawer/StyledMenu';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import { FormattedMessage } from 'react-intl';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import { CirclePicker } from 'react-color';
import TextFormatPanel from '../MyComponents/Drawer/Tools/TextFormatPanel';

const useStyles = makeStyles((theme) => ({
    editCard: {
        position: 'absolute',
        borderRadius: '4px',
        border: '1px solid rgba(0,0,0,0.3)',
        backgroundColor: 'transparent',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'stretch',
        zIndex: 0,
        '&:hover': {
            borderColor: 'rgba(87,78,51,0.8)',
        },
    },
    editCardActive: {
        boxShadow: '0 0 0 2px #07767D33',
        borderColor: '#07767D',
    },
    dragHandle: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'move',
        width: '20px',
        flexShrink: 0,
        boxSizing: 'border-box',
        color: 'rgba(0,0,0,0.3)',
        borderRight: '1px solid rgba(0,0,0,0.1)',
        touchAction: 'none',
        '&:hover': {
            color: 'rgba(0,0,0,0.5)',
        },
    },
    fieldsContainer: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minWidth: 0,
        position: 'relative',
    },
    textArea: {
        width: '100%',
        resize: 'none',
        border: 'none',
        backgroundColor: 'transparent',
        fontFamily: 'inherit',
        outline: 'none',
        padding: 0,
        margin: 0,
        boxSizing: 'border-box',
        lineHeight: 'normal',
        verticalAlign: 'top',
    },
    resizeHandle: {
        position: 'absolute',
        bottom: 0,
        right: 0,
        width: '12px',
        height: '12px',
        cursor: 'nwse-resize',
        touchAction: 'none',
        '&::after': {
            content: '""',
            position: 'absolute',
            bottom: '2px',
            right: '2px',
            width: '6px',
            height: '6px',
            borderRight: '2px solid rgba(0,0,0,0.3)',
            borderBottom: '2px solid rgba(0,0,0,0.3)',
        },
    },
    buttonRemoveComponent: {
        cursor: 'pointer',
        width: '0.7em',
        height: '0.7em',
        transition: 'transform 0.3s',
        '&:hover': {
            transform: 'scale(1.8)',
        },
    },
    menuText: {
        fontWeight: 400,
        fontSize: '0.875rem',
        lineHeight: '1.43rem',
        letterSpacing: '-0.01rem',
        paddingTop: '4px',
        paddingBottom: '4px',
    },
}));

const MAIN_MENU = 'MAIN_MENU';
const SIZE_MENU = 'SIZE_MENU';
const COLOR_MENU = 'COLOR_MENU';
const TEXT_FLUSH_INTERVAL = 2000;
const DRAG_HANDLE_WIDTH = 20; // handle width (border-box, includes its own border)

const TexBoxForSticky = React.forwardRef((props, ref) => {
    const classes = useStyles();

    let [toolWeight, setWeight] = useState(props.weight);
    let [color, setColor] = useState(props.color);
    let [text, setText] = useState(props.text);
    let [currentFontSize, setCurrentFontSize] = useState(props.fontSize);
    let [fontFamily, setFontFamily] = useState((props.fontFamily === 'Arial' ? null : props.fontFamily) || 'Arial, Helvetica, sans-serif');
    let [fontStyle, setFontStyle] = useState(props.fontStyle || 'normal');
    let [currentFontWeight, setCurrentFontWeight] = useState(props.fontWeight || 'normal');
    let [textDecoration, setTextDecoration] = useState(props.textDecoration || 'none');
    const [menuState, setMenuState] = useState(null);
    const [anchorMenu, setAnchorMenu] = useState(null);
    const [posX, setPosX] = useState(props.x);
    const [posY, setPosY] = useState(props.y);
    const [cardWidth, setCardWidth] = useState(props.width);
    let textAreaRef = useRef(null);
    let dragState = useRef(null);
    let resizeState = useRef(null);
    let flushWriting = useRef({ flushedValue: text, flushFunction: null, textToFlush: text });

    useImperativeHandle(ref, () => ({
        changeColor(color) {
            console.log('changing color', color);
        },
    }));

    useEffect(() => {
        setText(props.text);
        if (flushWriting.current.flushFunction) {
            clearTimeout(flushWriting.current.flushFunction);
        }
        flushWriting.current.flushFunction = null;
        flushWriting.current.flushedValue = props.text;
        flushWriting.current.textToFlush = props.text;
    }, [props.text]);

    useEffect(() => {
        setWeight(props.weight);
    }, [props.weight]);
    useEffect(() => {
        setColor(props.color);
    }, [props.color]);
    useEffect(() => {
        setPosX(props.x);
    }, [props.x]);
    useEffect(() => {
        setPosY(props.y);
    }, [props.y]);
    useEffect(() => {
        setCardWidth(props.width);
    }, [props.width]);
    useEffect(() => {
        setCurrentFontSize(props.fontSize);
    }, [props.fontSize]);
    useEffect(() => {
        setFontFamily((props.fontFamily === 'Arial' ? null : props.fontFamily) || 'Arial, Helvetica, sans-serif');
    }, [props.fontFamily]);
    useEffect(() => {
        setFontStyle(props.fontStyle || 'normal');
    }, [props.fontStyle]);
    useEffect(() => {
        setCurrentFontWeight(props.fontWeight || 'normal');
    }, [props.fontWeight]);
    useEffect(() => {
        setTextDecoration(props.textDecoration || 'none');
    }, [props.textDecoration]);

    // --- Drag logic (following LinkBoxForSticky pattern) ---
    const clampPosition = useCallback((x, y) => {
        let rect = drawUtils.getCurrentCanvasRect(props.email);
        let maxX = rect ? rect.width - 20 : Infinity;
        let maxY = rect ? rect.height - 20 : Infinity;
        return {
            x: Math.max(DRAG_HANDLE_WIDTH + 1, Math.min(x, maxX)),
            y: Math.max(0, Math.min(y, maxY)),
        };
    }, [props.email]);

    const onDragMove = useCallback((e) => {
        if (!dragState.current) return;
        let dx = e.clientX - dragState.current.startClientX;
        let dy = e.clientY - dragState.current.startClientY;
        let clamped = clampPosition(dragState.current.startX + dx, dragState.current.startY + dy);
        setPosX(clamped.x);
        setPosY(clamped.y);
    }, [clampPosition]);

    const onDragEnd = useCallback((e) => {
        if (!dragState.current) return;
        let dx = e.clientX - dragState.current.startClientX;
        let dy = e.clientY - dragState.current.startClientY;
        let clamped = clampPosition(dragState.current.startX + dx, dragState.current.startY + dy);
        let captureEl = dragState.current.captureEl;
        let pointerId = dragState.current.pointerId;
        dragState.current = null;
        document.removeEventListener('pointermove', onDragMove);
        document.removeEventListener('pointerup', onDragEnd);
        document.removeEventListener('pointercancel', onDragEnd);
        if (captureEl && pointerId != null && captureEl.hasPointerCapture && captureEl.hasPointerCapture(pointerId)) {
            captureEl.releasePointerCapture(pointerId);
        }

        let scaledPoint = drawUtils.scalePoint(props.startPoint, clamped.x, clamped.y);
        props.updateStickyTextInfo(props.id, text, scaledPoint.x, scaledPoint.y);
    }, [props.startPoint, props.id, props.updateStickyTextInfo, text, onDragMove, clampPosition]);

    const onDragStart = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        dragState.current = {
            startClientX: e.clientX,
            startClientY: e.clientY,
            startX: posX,
            startY: posY,
            pointerId: e.pointerId,
            captureEl: e.currentTarget,
        };
        if (e.currentTarget && e.currentTarget.setPointerCapture) {
            try {
                e.currentTarget.setPointerCapture(e.pointerId);
            } catch (err) { /* ignore */
            }
        }
        document.addEventListener('pointermove', onDragMove);
        document.addEventListener('pointerup', onDragEnd);
        document.addEventListener('pointercancel', onDragEnd);
    }, [posX, posY, onDragMove, onDragEnd]);

    // --- Resize logic ---
    const clampWidth = useCallback((w) => {
        let rect = drawUtils.getCurrentCanvasRect(props.email);
        let maxW = rect ? rect.width - posX : Infinity;
        return Math.max(60, Math.min(w, maxW));
    }, [props.email, posX]);

    const onResizeMove = useCallback((e) => {
        if (!resizeState.current) return;
        let dx = e.clientX - resizeState.current.startClientX;
        let newWidth = clampWidth(resizeState.current.startWidth + dx);
        setCardWidth(newWidth);
    }, [clampWidth]);

    const onResizeEnd = useCallback((e) => {
        if (!resizeState.current) return;
        let dx = e.clientX - resizeState.current.startClientX;
        let newWidth = clampWidth(resizeState.current.startWidth + dx);
        let captureEl = resizeState.current.captureEl;
        let pointerId = resizeState.current.pointerId;
        resizeState.current = null;
        document.removeEventListener('pointermove', onResizeMove);
        document.removeEventListener('pointerup', onResizeEnd);
        document.removeEventListener('pointercancel', onResizeEnd);
        if (captureEl && pointerId != null && captureEl.hasPointerCapture && captureEl.hasPointerCapture(pointerId)) {
            captureEl.releasePointerCapture(pointerId);
        }

        let scalePointSize = drawUtils.scalePoint({ x: 0, y: 0 }, newWidth, 0);
        let scaledPoint = drawUtils.scalePoint(props.startPoint, posX, posY);
        props.updateStickyTextInfo(props.id, text, scaledPoint.x, scaledPoint.y, scalePointSize.x, null);
    }, [props.startPoint, props.id, props.updateStickyTextInfo, text, posX, posY, onResizeMove]);

    const onResizeStart = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        resizeState.current = {
            startClientX: e.clientX,
            startWidth: cardWidth,
            pointerId: e.pointerId,
            captureEl: e.currentTarget,
        };
        if (e.currentTarget && e.currentTarget.setPointerCapture) {
            try {
                e.currentTarget.setPointerCapture(e.pointerId);
            } catch (err) { /* ignore */
            }
        }
        document.addEventListener('pointermove', onResizeMove);
        document.addEventListener('pointerup', onResizeEnd);
        document.addEventListener('pointercancel', onResizeEnd);
    }, [cardWidth, onResizeMove, onResizeEnd]);

    const textChange = (e) => {
        let newTxt = e.currentTarget.value;
        setText(newTxt);
        flushWriting.current.textToFlush = newTxt;

        if (!flushWriting.current.flushFunction) {
            flushWriting.current.flushFunction = setTimeout(() => {
                if (flushWriting.current.flushedValue !== flushWriting.current.textToFlush) {
                    props.updateStickyTextInfo(props.id, flushWriting.current.textToFlush);
                    flushWriting.current.flushedValue = flushWriting.current.textToFlush;
                }
                flushWriting.current.flushFunction = null;
            }, TEXT_FLUSH_INTERVAL);
        }
    };

    const flushNow = () => {
        if (flushWriting.current.flushedValue !== flushWriting.current.textToFlush) {
            if (flushWriting.current.textToFlush.length === 0) {
                props.removeElement(props.id);
            } else {
                props.updateStickyTextInfo(props.id, flushWriting.current.textToFlush);
            }
            flushWriting.current.flushedValue = flushWriting.current.textToFlush;
        } else {
            if (flushWriting.current.textToFlush.length === 0) {
                props.removeElement(props.id);
            }
        }
        if (flushWriting.current.flushFunction) {
            clearTimeout(flushWriting.current.flushFunction);
            flushWriting.current.flushFunction = null;
        }
        if (props.setWriting) {
            props.setWriting(false);
        }
    };

    const menuOpenTime = useRef(0);
    const handleMenuOpen = (event) => {
        if (!event) {
            handleClose();
            return;
        }
        menuOpenTime.current = Date.now();
        setAnchorMenu(event.currentTarget);
        setMenuState(MAIN_MENU);
    };
    const handleClose = () => {
        // Ignore close if triggered within 300ms of opening (touch event bleed)
        if (Date.now() - menuOpenTime.current < 300) return;
        setAnchorMenu(null);
        setMenuState(null);
    };

    // --- Font size computation ---
    let decremental = (window.zoomState.zoomLevel * 50 + 100) / 100;
    let canvasRect = drawUtils.getCurrentCanvasRect(props.email);
    let scaleFactor = canvasRect.width / drawUtils.CANVAS_ACTUAL_WIDTH;

    let baseFontSize;
    if (currentFontSize != null) {
        let canvas = drawUtils.getCanvas();
        let canvasHeight = (canvas && canvas.height) ? canvas.height : drawUtils.CANVAS_ACTUAL_HEIGHT;
        baseFontSize = canvasHeight * currentFontSize / 100;
    } else {
        baseFontSize = drawUtils.computeFontPixel(toolWeight);
    }
    let fontSizePx = scaleFactor * decremental * baseFontSize + 'px';

    let textStyle = {
        fontSize: fontSizePx,
        color: color,
        fontFamily: fontFamily,
        fontStyle: fontStyle,
        fontWeight: currentFontWeight,
        textDecoration: textDecoration,
    };

    if (props.isSelectable) {
        const updateOpts = (opts) => {
            props.updateStickyTextInfo(props.id, null, null, null, null, null, null, null, opts);
        };

        let menu;
        if (menuState === SIZE_MENU) {
            menu = (
                <TextFormatPanel
                    fontSize={currentFontSize}
                    fontWeight={toolWeight}
                    fontFamily={fontFamily}
                    fontStyle={fontStyle}
                    bold={currentFontWeight}
                    textDecoration={textDecoration}
                    showColor={false}
                    onFontSizeChange={(val) => {
                        setCurrentFontSize(val);
                        updateOpts({ fontSize: val });
                    }}
                    onFontFamilyChange={(val) => {
                        setFontFamily(val);
                        updateOpts({ fontFamily: val });
                    }}
                    onFontStyleChange={(val) => {
                        setFontStyle(val);
                        updateOpts({ fontStyle: val });
                    }}
                    onBoldChange={(val) => {
                        setCurrentFontWeight(val);
                        updateOpts({ fontWeight: val });
                    }}
                    onTextDecorationChange={(val) => {
                        setTextDecoration(val);
                        updateOpts({ textDecoration: val });
                    }}
                />
            );
        } else if (menuState === COLOR_MENU) {
            menu = (
                <div style={{ padding: '1rem' }}>
                    <CirclePicker
                        disableAlpha
                        color={color}
                        colors={[
                            '#000000', '#f44336', '#e91e63', '#9c27b0',
                            '#673ab7', '#3f51b5', '#2196f3', '#03a9f4',
                            '#009688', '#4caf50', '#8bc34a', '#cddc39',
                            '#ffeb3b', '#ffc107', '#ff9800', '#ff5722',
                            '#795548', '#607d8b',
                        ]}
                        onChangeComplete={(c) => {
                            setColor(c.hex);
                            props.updateStickyTextInfo(props.id, null, null, null, null, null, c.hex, null);
                            handleClose();
                        }}
                    />
                </div>
            );
        } else {
            menu = (
                <List>
                    <ListItem
                        button
                        onClick={() => {
                            props.setDialogClear(props.id);
                        }}
                        className={classes.menuText}
                    >
                        <ListItemIcon>
                            <DeleteForever color='secondary' />
                        </ListItemIcon>
                        <FormattedMessage id='tooltip.lessonPage.remove' />
                    </ListItem>
                    <ListItem
                        button
                        onClick={() => {
                            setMenuState(SIZE_MENU);
                        }}
                        className={classes.menuText}
                    >
                        <ListItemIcon>
                            <TextFormatIcon color='primary' />
                        </ListItemIcon>
                        <FormattedMessage id='tooltip.lessonPage.font' />
                    </ListItem>
                    <ListItem
                        button
                        onClick={() => {
                            setMenuState(COLOR_MENU);
                        }}
                        className={classes.menuText}
                    >
                        <ListItemIcon>
                            <span
                                style={{
                                    borderRadius: '50%',
                                    display: 'inline-block',
                                    border: '1px solid white',
                                    background: color,
                                    width: '24px',
                                    height: '24px',
                                }}
                            />
                        </ListItemIcon>
                        <FormattedMessage id='tooltip.lessonPage.color' />
                    </ListItem>
                </List>
            );
        }

        return (
            <>
                {/* Menu button — outside editCard so touch doesn't bleed to textarea */}
                {props.localSelectedItem === props.id && (
                    <div style={{
                        position: 'absolute',
                        left: (posX + cardWidth - 2) + 'px',
                        top: (posY - 20) + 'px',
                        zIndex: 2,
                    }}>
                        <MoreVert
                            color='primary'
                            onClick={(e) => {
                                handleMenuOpen(e);
                                e.preventDefault();
                                e.stopPropagation();
                            }}
                            onTouchStart={(e) => {
                                handleMenuOpen(e);
                                e.preventDefault();
                                e.stopPropagation();
                            }}
                            className={classes.buttonRemoveComponent}
                        />
                        <StyledMenu
                            id={`tex-box-edit-${props.id}`}
                            anchorEl={anchorMenu}
                            keepMounted
                            open={Boolean(anchorMenu)}
                            onClose={handleClose}
                        >
                            {menu}
                        </StyledMenu>
                    </div>
                )}
                <div
                    className={`${classes.editCard} ${props.localSelectedItem === props.id ? classes.editCardActive : ''}`}
                    style={{
                        position: 'absolute',
                        left: (posX - DRAG_HANDLE_WIDTH - 1) + 'px',
                        top: (posY - 1) + 'px',
                        width: (cardWidth + DRAG_HANDLE_WIDTH + 2) + 'px',
                    }}
                    onClick={(e) => {
                        e.stopPropagation();
                        props.setLocalSelectedItem(props.id);
                    }}
                    onTouchStart={(e) => {
                        e.stopPropagation();
                    }}
                >
                    {/* Drag handle */}
                    <div
                        className={classes.dragHandle}
                        onPointerDown={onDragStart}
                    >
                        <DragIndicatorIcon style={{ fontSize: '12px' }} />
                    </div>

                    {/* Content area */}
                    <div className={classes.fieldsContainer} style={{ width: cardWidth + 'px' }}>
                        <TextareaAutosize
                            value={text}
                            onChange={textChange}
                            className={classes.textArea}
                            style={textStyle}
                            ref={textAreaRef}
                            id={`text-area-sticky-${props.id}`}
                            onBlur={flushNow}
                        />
                        {/* Resize handle */}
                        <div
                            className={classes.resizeHandle}
                            onPointerDown={onResizeStart}
                        />
                    </div>
                </div>
            </>
        );
    } else {
        // Read-only mode
        let divStyle = {
            position: 'absolute',
            top: props.y + 'px',
            left: props.x + 'px',
            width: props.width + 'px',
            zIndex: '-1000',
        };
        let readOnlyStyle = {
            ...textStyle,
            width: '100%',
            resize: 'none',
            border: 'none',
            backgroundColor: 'transparent',
            cursor: 'default',
            padding: 0,
            margin: 0,
            lineHeight: 'normal',
            verticalAlign: 'top',
        };

        return (
            <div style={divStyle}>
                <TextareaAutosize value={text} style={readOnlyStyle} ref={textAreaRef} />
            </div>
        );
    }
});

export default TexBoxForSticky;
